import { FinancialPlanData } from '../types/financialPlan';
import { FinancialPlanSharePayload, FinancialPlanShareView } from '../types/financialPlanShare';
import { mergeFinancialPlanData, defaultFinancialPlanData } from './financialPlanDefaults';
import { resolvePlanAccountContributions } from './lgcAccountAllocation';
import { computeRequiredIncome, computeIncomePhaseMinimums } from './lifetimeGoalsCalc';
import { computeGrowthTimeline, retirementProjectionFromTimeline } from './lifetimeGoalsTimeline';
import { resolvePlanAges } from './planDates';

export function normalizeSharedPlanData(raw: unknown): FinancialPlanData {
  return resolvePlanAccountContributions(
    resolvePlanAges(mergeFinancialPlanData(raw as Partial<FinancialPlanData>, { preserveStored: true })),
  );
}

export function computeSharedPlanView(planName: string, rawPlan: unknown): FinancialPlanShareView {
  const plan = normalizeSharedPlanData(rawPlan);
  const result = computeRequiredIncome(plan);
  const timeline = computeGrowthTimeline(plan);
  const phaseMinimums = computeIncomePhaseMinimums(plan);
  const { onPaceAtRetire } = retirementProjectionFromTimeline(timeline);

  return {
    planName,
    plan,
    result,
    timeline,
    phaseMinimums,
    currentAge: plan.profile.currentAge,
    onPaceAtRetire,
    hasFullPlan: true,
  };
}

/** Build a view model from a share payload (live plan or legacy snapshot). */
export function sharePayloadToView(payload: FinancialPlanSharePayload): FinancialPlanShareView {
  if (payload.planData) {
    return computeSharedPlanView(payload.planName, payload.planData);
  }
  const legacy = payload.legacySnapshot;
  if (legacy && typeof legacy === 'object' && legacy.result && legacy.timeline) {
    const { onPaceAtRetire } = retirementProjectionFromTimeline(legacy.timeline);
    return {
      planName: payload.planName || legacy.planName,
      plan: defaultFinancialPlanData(),
      result: legacy.result,
      timeline: legacy.timeline,
      phaseMinimums: legacy.phaseMinimums ?? [],
      currentAge: legacy.currentAge,
      onPaceAtRetire,
      hasFullPlan: false,
    };
  }
  throw new Error('Share payload has no viewable plan data');
}
